import { Clear, Search } from "@mui/icons-material";
import { Box, IconButton, InputAdornment, TextField } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { PMButton } from "@pimo/pimo-components";
import { ControlledDateFormField } from "@pimo/pimo-components/src/lib/pimo-components/pimo-form/controlled-fields/controlled-date-field";
import { ControlledSelectField } from "@pimo/pimo-components/src/lib/pimo-components/pimo-form/controlled-fields/controlled-select-field";
import { FormFieldWrapper } from "@pimo/pimo-components/src/lib/pimo-components/pimo-form/form-field-wrapper";
import { Dayjs } from "dayjs";

import { getDateInputFormat } from "../../utils/format-date";

export type LogsSearchProps = {
  fromDate?: string | null;
  untilDate?: string | null;
  selectedProject: string;
  selectedEditor: string;
  searchTerm: string;
  projects: Array<{ id: string; name: string }>;
  editors: Array<{ id: string; name: string }>;
};

export type LogsSearchEventNames =
  | "search:from-date-changed"
  | "search:until-date-changed"
  | "search:project-changed"
  | "search:editor-changed"
  | "search:term-changed"
  | "search:clear-all"
  | "search:clear-search-term";

export type LogsSearchEventPayload = {
  fromDate?: string | null;
  untilDate?: string | null;
  selectedProject?: string;
  selectedEditor?: string;
  searchTerm?: string;
};

export const LogsSearch: PimoReactComponent<
  LogsSearchProps,
  LogsSearchEventNames,
  LogsSearchEventPayload
> = ({
  fireEvent,
  fromDate,
  untilDate,
  selectedProject,
  selectedEditor,
  searchTerm,
  projects,
  editors,
}) => {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3, borderBottom: "1px solid #e0e0e0" }}>
        <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
          <PMButton
            variant="outlined"
            size="medium"
            onClick={() => fireEvent?.("search:clear-all", {})}
            text="Clear All Filters"
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr 1fr 2fr",
            gap: 2,
          }}
        >
          <FormFieldWrapper label={`From`}>
            <ControlledDateFormField
              inputFormat={getDateInputFormat()}
              value={fromDate || null}
              onChange={(newValue: Dayjs | null) =>
                fireEvent?.("search:from-date-changed", {
                  fromDate: newValue?.toISOString(),
                })
              }
            />
          </FormFieldWrapper>

          <FormFieldWrapper label={`Until`}>
            <ControlledDateFormField
              inputFormat={getDateInputFormat()}
              value={untilDate || null}
              onChange={(newValue: Dayjs | null) => {
                return fireEvent?.("search:until-date-changed", {
                  untilDate: newValue?.toISOString(),
                });
              }}
            />
          </FormFieldWrapper>

          <Box>
            <FormFieldWrapper label={`Project`}>
              <ControlledSelectField
                value={selectedProject}
                onChange={(e) => {
                  fireEvent?.("search:project-changed", {
                    selectedProject: e as string,
                  });
                }}
                options={[
                  { value: "", label: "All" },
                  ...projects?.map((project) => ({
                    value: project.id,
                    label: project.name,
                  })),
                ]}
              ></ControlledSelectField>
            </FormFieldWrapper>
          </Box>

          <Box>
            <FormFieldWrapper label={`Editor`}>
              <ControlledSelectField
                value={selectedEditor}
                onChange={(e) => {
                  fireEvent?.("search:editor-changed", {
                    selectedEditor: e as string,
                  });
                }}
                options={[
                  { value: "", label: "All" },
                  ...editors?.map((editor) => ({
                    value: editor.id,
                    label: editor.name,
                  })),
                ]}
              ></ControlledSelectField>
            </FormFieldWrapper>
          </Box>

          <Box>
            <FormFieldWrapper label={`Search`}>
              <TextField
                value={searchTerm}
                onChange={(e) =>
                  fireEvent?.("search:term-changed", {
                    searchTerm: e.target.value,
                  })
                }
                placeholder="Enter search term"
                size="small"
                fullWidth
                sx={{
                  // single-line input
                  "& .MuiInputBase-input": {
                    fontSize: "14px",
                  },
                  // multiline textarea
                  "& .MuiInputBase-inputMultiline, & textarea": {
                    fontSize: "14px",
                  },
                  // label size (optional)
                  "& .MuiInputLabel-root": {
                    fontSize: "14px",
                  },
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: "text.secondary" }} />
                      </InputAdornment>
                    ),
                    ...(searchTerm && {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              fireEvent?.("search:clear-search-term", {});
                            }}
                            edge="end"
                          >
                            <Clear />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }),
                    style: {
                      borderRadius: "6px",
                      height: "45px",
                    },
                  },
                }}
              />
            </FormFieldWrapper>
          </Box>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};
